import { CheckCircleIcon, CheckIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { stringify } from 'query-string';
import React, { useState, useEffect } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import useRewardful from '@dashboard/hooks/useRewardful';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { stripePublicKey } from '@dashboard/services/env';
import { formatPrice } from '@dashboard/services/formatting';
import getCouponDiscount from '@dashboard/services/getCouponDiscount';
import { getPlan } from '@dashboard/services/plans';
import { fetcher } from '@dashboard/services/request';

import Button from '@dashboard/components/Button';
import Link from '@dashboard/components/Link';
import { InfoTooltip } from '@dashboard/components/Tooltip';

import { SubscriptionButton } from './BillingSettings/scenes/Plan/PlanOverview';
import { UpgradeForm } from './BillingSettings/scenes/Plan/Upgrade';
import scaleBrackets from '@dashboard/services/scaleBrackets';
import Lozenge from '@/dashboard/components/Lozenge';
import { PriceBreakdown } from './BillingSettings/scenes/Plan/Upgrade';

const PlansPage = () => {
  const { isTrialExpired, isCanceled, plan: sellerPlan, seller } = useAuth();
  const history = useHistory();

  const { billing_period: billingPeriodQuery, ...rest } = useUrlQuery();

  const [billingPeriod, setBillingPeriod] = useState(
    billingPeriodQuery || seller.planBillingPeriod || 'month',
  );
  const [selectedSalesRange, setSelectedSalesRange] = useState(10000);

  const isHighestSalesRange = selectedSalesRange > 100000;
  const { upgradeFlow = 'upgrade-page' } = useUrlQuery();

  function handleSetBillingPeriod(period) {
    history.replace(
      `/upgrade?${stringify({
        billing_period: period,
        ...rest,
      })}`,
    );
  }

  useEffect(() => {
    if (billingPeriodQuery && billingPeriodQuery !== billingPeriod) {
      setBillingPeriod(billingPeriodQuery);
      return;
    }

    if (seller.planBillingPeriod && seller.planBillingPeriod !== billingPeriod) {
      setBillingPeriod(seller.planBillingPeriod);
      return;
    }
  }, [billingPeriodQuery, seller.planBillingPeriod]);

  useEffect(() => {
    if (sellerPlan.bracket) {
      setSelectedSalesRange(sellerPlan.bracket);
      return;
    }

    if (sellerPlan.handle === 'grow') {
      setSelectedSalesRange(10000);
      return;
    }

    if (sellerPlan.handle === 'scale') {
      setSelectedSalesRange(30000);
      return;
    }
  }, [sellerPlan.bracket, sellerPlan.handle]);

  const currentPlan = getPlan(sellerPlan.handle, sellerPlan.version) || {};
  const isCurrentBillingPeriod = billingPeriod === seller.planBillingPeriod;

  const hasActiveSubscription = seller?.subscriptionStatus === 'active';

  return (
    <div className="container px-4 py-8">
      <div>
        <div className="flex items-center justify-between mb-8">
          {!isTrialExpired && !isCanceled && (
            <h1 className="text-2xl font-bold">Select your plan</h1>
          )}

          {isTrialExpired && (
            <div>
              <h1 className="text-2xl font-bold">Your trial has expired</h1>
              <p className="text-lg">Upgrade today to start accepting payments.</p>
            </div>
          )}

          {isCanceled && (
            <div>
              <h1 className="text-2xl font-bold">Your subscription has ended</h1>
              <p className="text-lg">Upgrade today to continue accepting payments.</p>
            </div>
          )}

          <div className="">
            <div className="flex justify-center items-center">
              <fieldset className="grid grid-cols-2 gap-x-1 rounded-full p-2 text-center leading-5 ring-1 ring-inset ring-gray-200 relative bg-white">
                <legend className="sr-only">Payment frequency</legend>
                <label
                  className={`cursor-pointer rounded-full py-1 px-4 font-medium ${
                    billingPeriod === 'month' ? 'bg-chocolate-1000 text-white' : 'text-gray-500'
                  }`}
                >
                  <input
                    type="radio"
                    name="frequency"
                    value="month"
                    className="sr-only"
                    checked={billingPeriod === 'month'}
                    onChange={() => handleSetBillingPeriod('month')}
                  />
                  <span>Monthly</span>
                </label>

                <label
                  className={`cursor-pointer rounded-full py-1 px-4 font-medium ${
                    billingPeriod === 'year' ? 'bg-chocolate-1000 text-white' : 'text-gray-500'
                  }`}
                >
                  <input
                    type="radio"
                    name="frequency"
                    value="year"
                    className="sr-only"
                    checked={billingPeriod === 'year'}
                    onChange={() => handleSetBillingPeriod('year')}
                  />
                  <span>Yearly - get 2 months free</span>
                </label>
              </fieldset>
            </div>
          </div>
        </div>

        <div className="">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-8 md:m-auto pb-8">
            <div
              className={clsx('shadow border-2 p-8 relative rounded-lg bg-white border-gray-100')}
            >
              <div className="px-4">
                <h3 className="flex items-center text-xl font-semibold pb-4">Launch</h3>

                <div
                  className={clsx('flex flex-col mb-4 lg:mb-0', {
                    'lg:h-56': billingPeriod === 'year',
                    'lg:h-48': billingPeriod === 'month',
                  })}
                >
                  <h4 className="text-4xl">
                    <span className="font-semibold">
                      {billingPeriod === 'month' ? '$29' : '$24'}
                    </span>{' '}
                    <span className="text-xl font-light text-gray-400 -ml-1">/ month</span>
                    <span className="mt-2 text-base font-light text-gray-400 block mb-2">
                      {billingPeriod === 'year' ? '($290 billed annually)' : ''}
                    </span>
                  </h4>

                  <span className=" inline-block text-base text-gray-600">
                    <span className="flex items-center gap-x-1">
                      Up to $3K sales per month{' '}
                      <InfoTooltip
                        label={
                          <>
                            We'll never stop you from taking payments. Check out our{' '}
                            <Link href="/help/articles/pricing-fees" target="blank" variant="light">
                              pricing guide
                            </Link>{' '}
                            for more information
                          </>
                        }
                      />
                    </span>
                  </span>
                </div>

                <div className="mb-4">
                  {currentPlan.handle === 'launch' &&
                  isCurrentBillingPeriod &&
                  hasActiveSubscription ? (
                    <div className="h-[54px] text-center rounded border border-transparent text-gray-600 flex items-center justify-center">
                      <Lozenge>Your current plan</Lozenge>
                    </div>
                  ) : (
                    <SubscriptionButton
                      plan={getPlan('launch')}
                      billingPeriod={billingPeriod}
                      setSelectedPlanHandle={() => {}}
                      upgradePath="/upgrade/subscribe"
                      upgradeFlow={upgradeFlow}
                    />
                  )}
                </div>

                <h4 className="text-sm font-normal text-gray-600 mt-4 pb-2">
                  Includes <span className="font-semibold text-black">99% of features</span>, plus:
                </h4>
                <ul className="flex flex-col gap-y-1 text-base text-gray-600">
                  <li className="flex items-center gap-x-1">
                    <CheckCircleIcon className="shrink-0 w-4 h-4 text-green-100" />
                    0% transaction fees{' '}
                    <InfoTooltip label="You'll only pay Stripe's processing fees" />
                  </li>
                  <li className="flex items-center gap-x-1">
                    <CheckCircleIcon className="shrink-0 w-4 h-4 text-green-100" />
                    Standard support <InfoTooltip label="Chat & email support" />
                  </li>
                  <li className="flex items-center gap-x-1">
                    <CheckCircleIcon className="shrink-0 w-4 h-4 text-green-100" />
                    Unlimited team members{' '}
                    <InfoTooltip label="Use Checkout Page with your entire team" />
                  </li>
                </ul>
              </div>
            </div>

            <div
              className={clsx(
                'shadow border-2 p-8 relative rounded-lg bg-white border-chocolate-1000',
              )}
            >
              <div className="absolute top-2 right-2">
                <div className="bg-emerald-100 text-emerald-800 text-sm font-semibold px-2 py-1 rounded">
                  Recommended
                </div>
              </div>
              <div className="px-4">
                <h3 className="flex items-center text-xl font-semibold pb-4">
                  {selectedSalesRange <= 10000 ? 'Grow' : 'Scale'}
                </h3>

                <div
                  className={clsx({
                    'lg:h-56 mb-4 lg:mb-0': billingPeriod === 'year',
                    'lg:h-48': billingPeriod === 'month',
                  })}
                >
                  <h4 className="text-4xl">
                    <span className="font-semibold">
                      {billingPeriod === 'month'
                        ? `${formatPrice({
                            amount: scaleBrackets.find(b => b.value === selectedSalesRange)
                              .monthlyPrice,
                            currency: 'USD',
                          })}`
                        : `${formatPrice({
                            amount: scaleBrackets.find(b => b.value === selectedSalesRange)
                              .yearlyPrice,
                            currency: 'USD',
                          })}`}
                      {selectedSalesRange > 100000 && '+'}
                    </span>{' '}
                    <span className="text-xl font-light text-gray-400 -ml-1">/ month</span>
                    <span className="mt-2 text-base font-light text-gray-400 block">
                      {billingPeriod === 'year'
                        ? `(${formatPrice({
                            amount: scaleBrackets.find(b => b.value === selectedSalesRange)
                              .yearlyPriceFull,
                            currency: 'USD',
                          })}${selectedSalesRange > 100000 ? '+' : ''} billed annually)`
                        : ''}
                    </span>
                  </h4>

                  <span className="block text-base text-gray-600">
                    <div className="relative w-full">
                      <select
                        className="w-full mt-2 border h-10 py-2 px-2 rounded-md outline-chocolate-600 border-chocolate-600 pr-8 appearance-none focus:ring-1 ring-chocolate-600"
                        value={selectedSalesRange}
                        onChange={e => setSelectedSalesRange(Number(e.target.value))}
                      >
                        {scaleBrackets.map(bracket => (
                          <option key={bracket.value} value={bracket.value}>
                            {bracket.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </span>

                  {isHighestSalesRange && (
                    <div className="text-sm block bg-chocolate-50 rounded-lg p-2 my-4">
                      <LightBulbIcon className="w-4 h-4 inline-block mr-1" />
                      Consider our Enterprise plan for lower fees and dedicated support.
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  {((currentPlan.handle === 'grow' && selectedSalesRange <= 10000) ||
                    (currentPlan.handle === 'scale' &&
                      currentPlan.salesRange === selectedSalesRange)) &&
                  isCurrentBillingPeriod &&
                  hasActiveSubscription ? (
                    <div className="h-[54px] text-center rounded border border-transparent text-gray-600 flex items-center justify-center">
                      <Lozenge>Your current plan</Lozenge>
                    </div>
                  ) : (
                    <>
                      {currentPlan.handle === 'scale' && selectedSalesRange > 1000000 ? (
                        <Button
                          appearance="primary"
                          size="full"
                          onClick={async () => {
                            if (!window.$crisp) return;

                            window.$crisp.push([
                              'set',
                              'message:text',
                              [`Hi! I want to discuss the Enterprise plan.`],
                            ]);

                            window.setTimeout(() => {
                              window.$crisp.push(['do', 'chat:open']);
                            }, 300);
                          }}
                          className="flex items-center mb-2"
                        >
                          <div>
                            Contact us
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth="2"
                              stroke="currentColor"
                              aria-hidden="true"
                              className="h-5 w-5 ml-2 inline"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M14 5l7 7m0 0l-7 7m7-7H3"
                              ></path>
                            </svg>
                          </div>
                        </Button>
                      ) : (
                        <SubscriptionButton
                          plan={getPlan(selectedSalesRange <= 10000 ? 'grow' : 'scale')}
                          billingPeriod={billingPeriod}
                          setSelectedPlanHandle={() => {}}
                          upgradePath="/upgrade/subscribe"
                          upgradeFlow={upgradeFlow}
                          selectedScaleBracket={selectedSalesRange}
                        />
                      )}
                    </>
                  )}
                </div>

                <h4 className="text-sm font-normal text-gray-600 mt-4 pb-2">
                  Everything in <span className="font-semibold text-black">Launch</span>, plus:
                </h4>
                <ul className="flex flex-col gap-y-1 text-base text-gray-600 pb-4">
                  <li className="flex items-center gap-x-1">
                    <CheckCircleIcon className="shrink-0 w-4 h-4 text-green-100" />
                    0% transaction fees{' '}
                    <InfoTooltip label="You'll only pay Stripe's processing fees" />
                  </li>
                  <li className="flex items-center gap-x-1">
                    <CheckCircleIcon className="shrink-0 w-4 h-4 text-green-100" />
                    Premium support <InfoTooltip label="Priority chat & email support" />
                  </li>
                  <li className="flex items-center gap-x-1">
                    <CheckCircleIcon className="shrink-0 w-4 h-4 text-green-100" /> Custom domain{' '}
                    <InfoTooltip label="Add your domain name to your checkout, event, and form pages" />
                  </li>
                  <li className="flex items-center gap-x-1">
                    <CheckCircleIcon className="shrink-0 w-4 h-4 text-green-100" /> Custom email
                    domain <InfoTooltip label="Send confirmation emails from your email address" />
                  </li>
                </ul>
              </div>
            </div>

            <div className="shadow border-2 border-gray-100 p-8 rounded-lg bg-white">
              <div className="px-4">
                <h3 className="flex items-center text-xl font-semibold pb-4">Enterprise</h3>

                <div
                  className={clsx({
                    'lg:h-56 mb-4 lg:mb-0': billingPeriod === 'year',
                    'lg:h-48': billingPeriod === 'month',
                  })}
                >
                  <h4 className="text-4xl">
                    <span className="font-semibold">Custom</span>{' '}
                    <span className="mt-2 text-base font-light text-gray-400 block"></span>
                    <span className="mt-2 text-base font-light text-gray-400 block mb-2 invisible">
                      {billingPeriod === 'year' ? '.' : ''}
                    </span>
                  </h4>

                  <span className="inline-block text-base text-gray-600">
                    <span className="flex items-center gap-x-1"></span>
                  </span>
                </div>

                <Button
                  appearance="primary"
                  size="full"
                  onClick={async () => {
                    if (!window.$crisp) return;

                    window.$crisp.push([
                      'set',
                      'message:text',
                      [`Hi! I want to discuss the Enterprise plan.`],
                    ]);

                    window.setTimeout(() => {
                      window.$crisp.push(['do', 'chat:open']);
                    }, 300);
                  }}
                  className="flex items-center mb-2"
                >
                  <div>
                    Contact us
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                      aria-hidden="true"
                      className="h-5 w-5 ml-2 inline"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      ></path>
                    </svg>
                  </div>
                </Button>

                <h4 className="text-sm font-normal text-gray-600 mt-4 pb-2">
                  Everything in{' '}
                  <span className="font-semibold text-black">
                    {selectedSalesRange <= 10000 ? 'Grow' : 'Scale'}
                  </span>
                  , plus:
                </h4>
                <ul className="flex flex-col gap-y-1 text-base text-gray-600 mb-4">
                  <li className="flex items-center gap-x-1">
                    <CheckCircleIcon className="shrink-0 w-4 h-4 text-green-100" />
                    0% transaction fees{' '}
                    <InfoTooltip label="You'll only pay Stripe's processing fees" />
                  </li>
                  <li className="flex items-center gap-x-1">
                    <CheckCircleIcon className="shrink-0 w-4 h-4 text-green-100" />
                    Dedicated support <InfoTooltip label="Reach out to the founders directly" />
                  </li>
                  <li className="flex items-center gap-x-1">
                    <CheckCircleIcon className="shrink-0 w-4 h-4 text-green-100" />
                    Custom pricing and billing terms
                  </li>
                  <li className="flex items-center gap-x-1">
                    <CheckCircleIcon className="shrink-0 w-4 h-4 text-green-100" />
                    Personalized onboarding
                  </li>
                  <li className="flex items-center gap-x-1">
                    <CheckCircleIcon className="shrink-0 w-4 h-4 text-green-100" />
                    Free migration
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center text-sm text-gray-500">
        Nonprofit, charity, or educational institution?{' '}
        <Link href="/help/articles/nonprofit-discount" className="underline" target="_blank">
          Get 50% off all plans
        </Link>
      </div>
    </div>
  );
};

const SubscribePage = () => {
  const { sellerId, token } = useAuth();
  const {
    plan: planHandleQuery,
    billing_period: billingPeriodQuery,
    coupon: couponQuery,
    upgradeFlow: upgradeFlowQuery,
    scaleBracket: scaleBracketQuery,
  } = useUrlQuery();

  const [planHandle] = useState(planHandleQuery || 'launch');
  const [billingPeriod, setBillingPeriod] = useState(billingPeriodQuery || 'month');
  const [couponCode] = useState(couponQuery);
  const [upgradeFlow, setUpgradeFlow] = useState(upgradeFlowQuery || 'upgrade-page');

  const { coupon: rewardfulCoupon } = useRewardful();

  const { data: coupon } = useQuery({
    queryKey: ['coupon', couponCode, rewardfulCoupon, sellerId],
    queryFn: () =>
      fetcher(
        `/sellers/${sellerId}/validate-coupon?coupon=${couponCode || rewardfulCoupon.id}`,
        token,
      ),
    enabled: !!sellerId && (!!couponCode || !!rewardfulCoupon),
    retry: false,
  });

  const upgradePlan = getPlan(planHandle);

  function switchBillingPeriodToYearly() {
    setBillingPeriod('year');
    setUpgradeFlow('upgrade-page-yearly-nudge');
  }

  return (
    <Elements stripe={loadStripe(stripePublicKey)} options={{}}>
      <div className="max-w-xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Upgrade to {upgradePlan.title}</h1>

          <PriceBreakdown
            plan={upgradePlan}
            coupon={coupon}
            billingPeriod={billingPeriod}
            scaleBracket={Number(scaleBracketQuery)}
          />

          <p className="text-black mb-4">Complete your details below to start your subscription</p>

          {billingPeriod === 'month' && (
            <div className="w-full rounded border-dashed border-2 border-chocolate-1000 bg-chocolate-50 p-4 text-lg font-semibold flex items-center justify-between my-6 ">
              <div>
                Get <span className="text-chocolate-1000">2 months for free</span> by switching to
                yearly
              </div>
              <Button
                appearance="primary"
                onClick={switchBillingPeriodToYearly}
                className="ml-auto"
              >
                Accept
              </Button>
            </div>
          )}
        </div>

        <div className="max-w-xl m-auto">
          <UpgradeForm
            onSuccessPath="/"
            planHandle={planHandle}
            billingPeriod={billingPeriod}
            couponQuery={couponQuery}
            upgradeFlow={upgradeFlow}
            scaleBracket={Number(scaleBracketQuery)}
          />

          <div className="mt-8 text-center">
            <Link
              to={`/upgrade?${stringify({
                plan: planHandle,
                billing_period: billingPeriod,
                coupon: couponQuery,
                upgradeFlow,
                scaleBracket: Number(scaleBracketQuery),
              })}`}
              variant="secondary"
            >
              Back
            </Link>
          </div>
        </div>
      </div>
    </Elements>
  );
};

const UpgradePage = () => {
  return (
    <Switch>
      <Route path="/upgrade" exact component={PlansPage} />

      <Route path="/upgrade/subscribe" exact component={SubscribePage} />
    </Switch>
  );
};

export default UpgradePage;
